/**
 * @file Auction group list/create hub with trial banner and navigation actions.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAuctionGroups, createAuctionGroup } from '../api/auctionGroups';
import { queryKeys } from '../api/queryKeys';
import { Plus, Loader, Calendar, FileUp, AlertTriangle, PenLine, Users, BarChart3 } from 'lucide-react';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { useAuth } from '../contexts/AuthContext';
import PaginationControls from '../components/PaginationControls';
import { DEFAULT_CARD_PAGE_SIZE, clampOffset } from '../utils/pagination';
import { INPUT_MAX_LENGTH, clampInput } from '../utils/inputLimits';
import { daysRemaining, formatBytes } from '../utils/billing';

function Home() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManageGroups, company, refresh } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_CARD_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  const trialDaysLeft = daysRemaining(company?.trialEndsAt);
  const atGroupLimit =
    company?.status === 'trial' &&
    company?.auctionGroupLimit != null &&
    (company.auctionGroupCount ?? 0) >= company.auctionGroupLimit;

  const { data: auctionGroupsData, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.auctionGroups(),
    queryFn: getAuctionGroups,
  });

  // Ensure auctionGroups is always an array, even if API returns something else
  const auctionGroups = Array.isArray(auctionGroupsData) ? auctionGroupsData : [];

  const safeOffset = useMemo(
    () => clampOffset(offset, auctionGroups.length, pageSize),
    [offset, auctionGroups.length, pageSize]
  );

  useEffect(() => {
    if (safeOffset !== offset) setOffset(safeOffset);
  }, [safeOffset, offset]);

  const pagedGroups = useMemo(
    () => auctionGroups.slice(safeOffset, safeOffset + pageSize),
    [auctionGroups, safeOffset, pageSize]
  );

  const createMutation = useMutation({
    mutationFn: (title) => {
      console.log('Creating auction group with title:', title);
      return createAuctionGroup(title);
    },
    onSuccess: async (data) => {
      console.log('Auction group created successfully:', data);
      await queryClient.invalidateQueries({ queryKey: queryKeys.auctionGroups() });
      await refetch();
      try {
        await refresh();
      } catch {
        /* ignore */
      }
      setShowCreateModal(false);
      setNewGroupTitle('');
    },
    onError: (error) => {
      console.error('Error creating auction group:', error);
      console.error('Error details:', {
        message: error.message,
        statusCode: error.statusCode,
        response: error.response,
      });
    },
  });

  const handleCreateGroup = (e) => {
    e.preventDefault();
    const title = newGroupTitle.trim();
    if (!title) return;
    createMutation.mutate(title);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMergeData = (e, groupId) => {
    e.stopPropagation();
    navigate(`/merge?auctionGroupId=${groupId}`);
  };

  const handleViewBidders = (e, groupId) => {
    e.stopPropagation();
    navigate(`/bidders?auctionGroupId=${groupId}`);
  };

  const handleHighVolumeReport = (e, groupId) => {
    e.stopPropagation();
    navigate(`/high-volume?auctionGroupId=${groupId}`);
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="loading">
          <Loader className="spin" size={40} />
          <p>Loading auction groups...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isRateLimit = error?.statusCode === 429 || error?.message?.includes('429') || error?.message?.includes('Too many requests');
    
    return (
      <div className="container">
        <div className="error">
          <AlertTriangle size={20} aria-hidden />
          <strong>Error loading auction groups:</strong>
          <div style={{ marginTop: '10px' }}>
            {isRateLimit ? (
              <div>
                <p><strong>⚠️ Rate Limit Exceeded</strong></p>
                <p style={{ marginTop: '10px' }}>
                  Too many requests have been made from this IP address. This can happen when multiple users or devices are accessing the site simultaneously.
                </p>
                <p style={{ marginTop: '10px' }}>
                  Please wait a few minutes and try again. The rate limit resets every 15 minutes.
                </p>
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#6c757d' }}>
                  If this happens frequently, the server administrator can increase the rate limit threshold.
                </p>
              </div>
            ) : (
              error.message || 'Unknown error occurred'
            )}
          </div>
          <button 
            onClick={() => refetch()} 
            className="btn btn-primary"
            style={{ marginTop: '15px' }}
            disabled={isRateLimit}
          >
            {isRateLimit ? 'Please wait...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // Log if we got unexpected data format (for debugging)
  if (!isLoading && !Array.isArray(auctionGroupsData) && auctionGroupsData !== undefined) {
    console.warn('Unexpected auctionGroups data format:', auctionGroupsData);
  }

  return (
    <div className="container">
      {company?.status === 'trial' && (
        <div className="trial-banner" role="status">
          <strong>Free trial</strong>
          {trialDaysLeft != null && <> · {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left</>}
          {company.uploadBytesLimit != null && (
            <>
              {' '}
              · Upload {formatBytes(company.uploadBytesUsed)} / {formatBytes(company.uploadBytesLimit)}
            </>
          )}
          {company.auctionGroupLimit != null && (
            <>
              {' '}
              · {company.auctionGroupCount ?? 0}/{company.auctionGroupLimit} auction group
            </>
          )}
        </div>
      )}
      <div className="card">
        <div className="page-header">
          <h2 className="page-title">Auction Groups{company ? ` — ${company.name}` : ''}</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {canManageGroups && !atGroupLimit && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn btn-primary"
              >
                <Plus size={18} />
                Create New Auction Group
              </button>
            )}
          </div>
        </div>

        {canManageGroups && showCreateModal && (
          <div className="modal-overlay" onClick={() => {
            setShowCreateModal(false);
            setNewGroupTitle('');
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create New Auction Group</h3>
              </div>
              <form onSubmit={handleCreateGroup}>
                <div className="form-group">
                  <label htmlFor="groupTitle" className="form-label">
                    Title
                  </label>
                  <input
                    id="groupTitle"
                    type="text"
                    className="input"
                    placeholder="Enter auction group title"
                    value={newGroupTitle}
                    onChange={(e) => setNewGroupTitle(clampInput(e.target.value))}
                    maxLength={INPUT_MAX_LENGTH}
                    required
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewGroupTitle('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createMutation.isPending || !newGroupTitle.trim()}
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader size={16} className="spin" />
                        Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </form>
              {createMutation.isError && (
                <div className="error" style={{ marginTop: '15px' }}>
                  <AlertTriangle size={16} aria-hidden />
                  {createMutation.error?.message || 'Failed to create auction group'}
                </div>
              )}
            </div>
          </div>
        )}

        {auctionGroups.length === 0 ? (
          <div className="empty-state">
            <p>No auction groups yet</p>
            <p>Click "Create New Auction Group" to get started</p>
          </div>
        ) : (
          <>
          <div style={{ display: 'grid', gap: '20px' }}>
            {pagedGroups.map((group) => (
              <div key={group.id} className="auction-group-card">
                <div className="auction-group-header">
                  <div>
                    <h3 className="auction-group-title">
                      {decodeHtmlEntities(group.title) || `Auction Group #${group.id}`}
                    </h3>
                    <p className="auction-group-id">ID: {group.id}</p>
                  </div>
                </div>

                <div className="auction-group-meta">
                  <div>
                    <div className="meta-item">
                      <Calendar size={16} color="var(--color-gray-600)" />
                      <strong className="meta-label">Created</strong>
                    </div>
                    <p className="meta-value">{formatDate(group.created_at)}</p>
                  </div>

                  <div>
                    <div className="meta-item">
                      <PenLine size={16} color="var(--color-gray-600)" aria-hidden />
                      <strong className="meta-label">Last Modified</strong>
                    </div>
                    <p className="meta-value">{formatDate(group.updated_at)}</p>
                  </div>
                </div>

                <div className="auction-group-section">
                  <div className="auction-group-section-label">Auction IDs:</div>
                  {group.auction_ids && group.auction_ids.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                      {group.auction_ids.map((auctionId, idx) => (
                        <a
                          key={idx}
                          href={`https://www.equip-bid.com/auction/${auctionId}/inventory`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="auction-id-badge"
                        >
                          {auctionId}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: 'var(--color-gray-600)', fontStyle: 'italic', fontSize: '14px', marginBottom: '10px' }}>
                      No auction IDs yet
                    </p>
                  )}
                  <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                    <strong>Lots:</strong> {group.lot_count || 0}
                  </div>
                </div>

                <div className="auction-group-actions">
                  <button
                    onClick={(e) => handleViewBidders(e, group.id)}
                    className="btn btn-success"
                  >
                    <Users size={16} aria-hidden />
                    View Bidders
                  </button>
                  <button
                    onClick={(e) => handleHighVolumeReport(e, group.id)}
                    className="btn btn-warning"
                  >
                    <BarChart3 size={16} aria-hidden />
                    High Volume Report
                  </button>
                  <button
                    onClick={(e) => handleMergeData(e, group.id)}
                    className="btn btn-primary"
                  >
                    <FileUp size={16} />
                    Merge Data & Settings
                  </button>
                </div>
              </div>
            ))}
          </div>
          <PaginationControls
            total={auctionGroups.length}
            limit={pageSize}
            offset={safeOffset}
            onChange={({ limit, offset: nextOffset }) => {
              setPageSize(limit);
              setOffset(nextOffset);
            }}
          />
          </>
        )}
      </div>
    </div>
  );
}

export default Home;

